import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { BankReconciliationService } from '../bank-reconciliation.service';
import {
    BankReconciliationAuditEvent,
    BankReconciliationStatementReport,
    BankStatementLineStatus
} from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-reconciliation-statement-report',
    standalone: true,
    imports: [CommonModule, ButtonModule, TableModule, TagModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Reconciliation Statement" [title]="title" [subtitle]="subtitle">
                <div actions>
                    <button pButton type="button" icon="pi pi-sliders-h" label="Workspace" [outlined]="true" (click)="openWorkspace()"></button>
                    <button pButton type="button" icon="pi pi-list" label="Statement" [outlined]="true" *ngIf="report?.statement?.statementId" (click)="openStatement()"></button>
                    <button pButton type="button" icon="pi pi-refresh" label="Refresh" [loading]="loading" (click)="load()"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <div class="summary-grid" *ngIf="report">
                <section class="summary-card">
                    <span>Opening Balance</span>
                    <strong>{{ amount(report.openingBalance) }} {{ currency }}</strong>
                </section>
                <section class="summary-card">
                    <span>Closing Balance</span>
                    <strong>{{ amount(report.closingBalance) }} {{ currency }}</strong>
                </section>
                <section class="summary-card">
                    <span>Matched Amount</span>
                    <strong>{{ amount(report.matchedAmount) }} {{ currency }}</strong>
                </section>
                <section class="summary-card risk" [class.clean]="!report.variance">
                    <span>Variance</span>
                    <strong>{{ amount(report.variance) }} {{ currency }}</strong>
                </section>
            </div>

            <div class="panel-grid">
                <app-page-data-panel title="Line Summary" resultText="Bank statement line states">
                    <div class="line-summary" *ngIf="report">
                        <div><span>Matched</span><strong>{{ report.matchedLineCount }}</strong><small>{{ amount(report.matchedAmount) }}</small></div>
                        <div><span>Unmatched</span><strong>{{ report.unmatchedLineCount }}</strong><small>{{ amount(report.unmatchedAmount) }}</small></div>
                        <div><span>Partial</span><strong>{{ report.partialLineCount }}</strong><small>{{ amount(report.partialAmount) }}</small></div>
                        <div><span>Exception</span><strong>{{ report.exceptionLineCount }}</strong><small>{{ amount(report.exceptionAmount) }}</small></div>
                        <div><span>Excluded</span><strong>{{ report.excludedLineCount }}</strong><small>{{ amount(report.excludedAmount) }}</small></div>
                    </div>
                </app-page-data-panel>

                <app-page-data-panel title="Session Facts" resultText="Current lifecycle and period">
                    <dl class="facts" *ngIf="report">
                        <div><dt>Status</dt><dd><p-tag [value]="report.session.status" severity="info"></p-tag></dd></div>
                        <div><dt>Period</dt><dd>{{ report.session.periodStartDate || '-' }} to {{ report.session.periodEndDate || '-' }}</dd></div>
                        <div><dt>Book Balance</dt><dd>{{ amount(report.session.bookBalance) }}</dd></div>
                        <div><dt>Bank Balance</dt><dd>{{ amount(report.session.bankBalance) }}</dd></div>
                        <div><dt>Adjusted Book</dt><dd>{{ amount(report.session.adjustedBookBalance) }}</dd></div>
                        <div><dt>Adjusted Bank</dt><dd>{{ amount(report.session.adjustedBankBalance) }}</dd></div>
                    </dl>
                </app-page-data-panel>
            </div>

            <app-page-data-panel title="Unmatched / Partial / Exception Bank Lines" [resultText]="(report?.unmatchedBankLines?.length || 0) + ' lines'">
                <p-table [value]="report?.unmatchedBankLines || []" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header">
                        <tr><th>Line</th><th>Date</th><th>Reference</th><th>Description</th><th>Open Amount</th><th>Status</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-line>
                        <tr>
                            <td>#{{ line.lineNumber }}</td>
                            <td>{{ line.transactionDate || '-' }}</td>
                            <td>{{ line.bankReference || line.customerReference || '-' }}</td>
                            <td>{{ line.description || '-' }}</td>
                            <td>{{ amount(lineOpenAmount(line)) }} {{ currency }}</td>
                            <td><p-tag [value]="line.status" [severity]="lineSeverity(line.status)"></p-tag></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="6">No open bank lines in this report.</td></tr></ng-template>
                </p-table>
            </app-page-data-panel>

            <div class="panel-grid">
                <app-page-data-panel title="Active Matches" [resultText]="(report?.activeMatches?.length || 0) + ' matches'">
                    <p-table [value]="report?.activeMatches || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Match</th><th>Bank Line</th><th>Amount</th><th>Items</th></tr></ng-template>
                        <ng-template pTemplate="body" let-match>
                            <tr>
                                <td><strong>#{{ match.match.matchId }}</strong><div class="muted">{{ match.match.matchType }} | {{ match.match.matchStatus }}</div></td>
                                <td>#{{ match.statementLine?.lineNumber || match.match.statementLineId }}</td>
                                <td>{{ amount(match.match.matchedAmount) }} {{ currency }}</td>
                                <td>{{ match.items?.length || 0 }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="4">No active matches.</td></tr></ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Uncleared Book Items" [resultText]="(report?.unclearedBookItems?.length || 0) + ' items'">
                    <p-table [value]="report?.unclearedBookItems || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Entry</th><th>Date</th><th>Source</th><th>Open</th></tr></ng-template>
                        <ng-template pTemplate="body" let-item>
                            <tr>
                                <td><strong>{{ item.entryNumber || ('Item #' + item.journalItemId) }}</strong><div class="muted">{{ item.name || item.reference || '-' }}</div></td>
                                <td>{{ item.entryDate || '-' }}</td>
                                <td>{{ item.sourceType }} {{ item.sourceNumber || '' }}</td>
                                <td>{{ amount(item.openAmount) }} {{ item.currencyCode || currency }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="4">No uncleared book items returned.</td></tr></ng-template>
                    </p-table>
                </app-page-data-panel>
            </div>

            <div class="panel-grid">
                <app-page-data-panel title="Adjustments" [resultText]="(report?.adjustments?.length || 0) + ' adjustments'">
                    <p-table [value]="report?.adjustments || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Type</th><th>Status</th><th>Amount</th><th>Journal</th></tr></ng-template>
                        <ng-template pTemplate="body" let-adjustment>
                            <tr>
                                <td>{{ adjustment.adjustmentType }}</td>
                                <td><p-tag [value]="adjustment.status" severity="info"></p-tag></td>
                                <td>{{ amount(adjustment.amount) }} {{ currency }}</td>
                                <td>{{ adjustment.journalEntryId || '-' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="4">No adjustments.</td></tr></ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="Exceptions" [resultText]="(report?.exceptions?.length || 0) + ' exceptions'">
                    <p-table [value]="report?.exceptions || []" [loading]="loading" styleClass="p-datatable-sm">
                        <ng-template pTemplate="header"><tr><th>Type</th><th>Severity</th><th>Status</th><th>Description</th></tr></ng-template>
                        <ng-template pTemplate="body" let-exception>
                            <tr>
                                <td>{{ exception.exceptionType }}</td>
                                <td>{{ exception.severity }}</td>
                                <td><p-tag [value]="exception.status" severity="warn"></p-tag></td>
                                <td>{{ exception.description || '-' }}</td>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="emptymessage"><tr><td colspan="4">No exceptions.</td></tr></ng-template>
                    </p-table>
                </app-page-data-panel>
            </div>

            <app-page-data-panel title="Audit Timeline" [resultText]="(report?.auditEvents?.length || 0) + ' events'">
                <p-table [value]="report?.auditEvents || []" [loading]="loading" styleClass="p-datatable-sm">
                    <ng-template pTemplate="header"><tr><th>Time</th><th>Action</th><th>Status Change</th><th>User</th><th>Comment</th></tr></ng-template>
                    <ng-template pTemplate="body" let-event>
                        <tr>
                            <td>{{ event.performedAt || '-' }}</td>
                            <td><strong>{{ event.action }}</strong></td>
                            <td>{{ statusChange(event) }}</td>
                            <td>{{ event.performedBy || '-' }}</td>
                            <td>{{ event.comment || '-' }}</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage"><tr><td colspan="5">No audit events.</td></tr></ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: .85rem; margin-bottom: 1rem; }
        .summary-card { border: 1px solid #e2e8f0; border-radius: 16px; background: #fff; padding: 1rem; display: flex; flex-direction: column; gap: .35rem; }
        .summary-card span { color: #64748b; font-size: .78rem; text-transform: uppercase; letter-spacing: .04em; }
        .summary-card strong { color: #0f172a; font-size: 1.15rem; }
        .summary-card.risk strong { color: #b91c1c; }
        .summary-card.clean strong { color: #047857; }
        .panel-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 1rem; }
        .line-summary { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: .75rem; }
        .line-summary div { border: 1px solid #e2e8f0; border-radius: 14px; padding: .8rem; }
        .line-summary span, .line-summary small { display: block; color: #64748b; }
        .line-summary strong { display: block; color: #0f172a; font-size: 1.4rem; }
        .facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .7rem; margin: 0; }
        .facts div { border-bottom: 1px solid #e2e8f0; padding-bottom: .5rem; }
        .facts dt { color: #64748b; font-size: .78rem; }
        .facts dd { margin: .15rem 0 0; color: #0f172a; font-weight: 600; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        @media (max-width: 1000px) { .summary-grid, .panel-grid, .line-summary, .facts { grid-template-columns: 1fr; } }
    `]
})
export class BankReconciliationStatementReportComponent implements OnInit {
    sessionId = 0;
    loading = false;
    report?: BankReconciliationStatementReport;
    metrics: PageMetric[] = [];

    constructor(
        private route: ActivatedRoute,
        private service: BankReconciliationService,
        public router: Router
    ) {}

    get title(): string {
        const statementNumber = this.report?.statement?.statementNumber;
        return statementNumber ? `Statement Report ${statementNumber}` : `Session Report #${this.sessionId}`;
    }

    get subtitle(): string {
        if (!this.report) return 'Loading reconciliation statement report.';
        return `Session ${this.report.session.status} | ${this.report.session.periodStartDate || '-'} to ${this.report.session.periodEndDate || '-'}`;
    }

    get currency(): string {
        return this.report?.statement?.currencyCode || 'SAR';
    }

    ngOnInit(): void {
        this.sessionId = Number(this.route.snapshot.paramMap.get('sessionId')) || 0;
        this.load();
    }

    load(): void {
        if (!this.sessionId) return;
        this.loading = true;
        this.service.getSessionReport(this.sessionId).subscribe({
            next: report => {
                this.report = report;
                this.metrics = [
                    { label: 'Matched lines', value: report.matchedLineCount, tone: 'success' },
                    { label: 'Unmatched lines', value: report.unmatchedLineCount, tone: report.unmatchedLineCount ? 'warn' : 'success' },
                    { label: 'Partial lines', value: report.partialLineCount, tone: report.partialLineCount ? 'warn' : 'success' },
                    { label: 'Exceptions', value: report.exceptionLineCount, tone: report.exceptionLineCount ? 'danger' : 'success' },
                    { label: 'Variance', value: this.amount(report.variance), tone: report.variance ? 'danger' : 'success' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    openWorkspace(): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/sessions', this.sessionId]);
    }

    openStatement(): void {
        const statementId = this.report?.statement?.statementId;
        if (statementId) this.router.navigate(['/accounts/enterprise/bank-reconciliation/statements', statementId]);
    }

    amount(value?: number): string {
        return (value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    lineOpenAmount(line: { signedAmount?: number; matchedAmount?: number }): number {
        return Math.round((Math.abs(Number(line.signedAmount || 0)) - Number(line.matchedAmount || 0)) * 10000) / 10000;
    }

    lineSeverity(status: BankStatementLineStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'MATCHED') return 'success';
        if (status === 'PARTIAL') return 'warn';
        if (status === 'EXCEPTION') return 'danger';
        if (status === 'EXCLUDED') return 'secondary';
        return 'info';
    }

    statusChange(event: BankReconciliationAuditEvent): string {
        if (!event.beforeStatus && !event.afterStatus) return '-';
        return `${event.beforeStatus || '-'} -> ${event.afterStatus || '-'}`;
    }
}
